import React from 'react'

import { Theme, useTheme, IconButton, Badge } from '@material-ui/core'
import { format } from 'date-fns'
import { ChatBubbleOutline } from '@material-ui/icons'

import { Attachment, Container } from '../../components'
import { SupportTicket, SupportTicketStatus } from '../../models'
import { formatPhoneForDisplay, normalizeString } from '../../helpers'
import { SupportTicketStatusBadge } from '../../components/SupportTicketStatusBadge'
import { SupportTicketTransitionButtons } from '../../components/SupportTicketTransitionButtons'
import { typographyStyle } from '../../theme/typographyStyle'

interface SupportTicketRowProps {
    ticket: SupportTicket
    theme: Theme
    handleTransition: (
        e: React.MouseEvent<HTMLButtonElement>,
        ticket: SupportTicket,
        status: SupportTicketStatus,
    ) => void
    handleOpenMessageDrawer: (ticket: SupportTicket) => void
}

export const SupportTicketRow: React.FC<SupportTicketRowProps> = ({
    ticket,
    theme,
    handleTransition,
    handleOpenMessageDrawer,
}) => {
    return (
        <Container
            style={{
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.borderRadius,
                padding: theme.spacing(1.5),
                marginBottom: theme.spacing(1),
                flexDirection: 'column',
                gap: theme.spacing(1.5),
            }}
        >
            {/* Header Section */}
            <Container
                style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: theme.spacing(1.5),
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
            >
                <Container
                    style={{ alignItems: 'center', gap: theme.spacing(2) }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 'bold',
                        }}
                    >
                        Ticket #{ticket.id}
                    </span>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            color: theme.palette.text.secondary,
                        }}
                    >
                        {ticket.created_date
                            ? format(
                                  new Date(ticket.created_date),
                                  'MMM dd, yyyy',
                              )
                            : 'N/A'}
                    </span>
                </Container>
                <Container
                    style={{ alignItems: 'center', gap: theme.spacing(1) }}
                >
                    {ticket.channel && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation()
                                handleOpenMessageDrawer(ticket)
                            }}
                        >
                            <Badge
                                badgeContent={ticket.message_count}
                                color={
                                    ticket.message_count > 0
                                        ? 'primary'
                                        : 'secondary'
                                }
                            >
                                <ChatBubbleOutline />
                            </Badge>
                        </IconButton>
                    )}
                    <SupportTicketStatusBadge
                        status={ticket.status}
                        transitions={ticket.transitions}
                        createdDate={ticket.created_date}
                    />
                </Container>
            </Container>

            {/* Main Content Section */}
            <Container
                style={{
                    gap: theme.spacing(2),
                    paddingBottom: theme.spacing(1.5),
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
            >
                {/* Left Column - User Info */}
                <Container
                    style={{
                        flexDirection: 'column',
                        gap: theme.spacing(1),
                        flex: 1.5,
                        minWidth: '250px',
                    }}
                >
                    <Container style={{ gap: theme.spacing(2) }}>
                        <span style={{ fontWeight: 'bold' }}>
                            {ticket.name}
                        </span>
                        <span style={{ color: theme.palette.text.secondary }}>
                            ({ticket.is_vendor ? 'Vendor' : 'Property User'})
                        </span>
                    </Container>
                    <Container style={{ gap: theme.spacing(2) }}>
                        {ticket.email && (
                            <span
                                style={{ color: theme.palette.text.secondary }}
                            >
                                {ticket.email}
                            </span>
                        )}
                        <span style={{ color: theme.palette.text.secondary }}>
                            {formatPhoneForDisplay(ticket.phone_number) ||
                                'N/A'}
                        </span>
                    </Container>
                    <span style={{ color: theme.palette.text.secondary }}>
                        {ticket.user?.active_workspace?.name ||
                            ticket.property_info ||
                            'N/A'}
                    </span>
                </Container>

                {/* Middle Column - Category Info */}
                <Container
                    style={{
                        flexDirection: 'column',
                        gap: theme.spacing(0.5),
                        flex: 1,
                        minWidth: '200px',
                    }}
                >
                    <Container style={{ gap: theme.spacing(1) }}>
                        <span style={{ fontWeight: 'bold' }}>Category:</span>
                        <span style={{ color: theme.palette.text.secondary }}>
                            {ticket.category
                                ? normalizeString(ticket.category as string)
                                : 'N/A'}
                        </span>
                    </Container>
                    <Container style={{ gap: theme.spacing(1) }}>
                        <span style={{ fontWeight: 'bold' }}>
                            Sub-category:
                        </span>
                        <span style={{ color: theme.palette.text.secondary }}>
                            {ticket.sub_category
                                ? normalizeString(ticket.sub_category as string)
                                : 'N/A'}
                        </span>
                    </Container>
                    <Container style={{ gap: theme.spacing(1) }}>
                        <span style={{ fontWeight: 'bold' }}>Source:</span>
                        <span style={{ color: theme.palette.text.secondary }}>
                            {ticket.twilio_call_event
                                ? 'Phone Call'
                                : 'Web Form'}
                        </span>
                    </Container>
                    {ticket.completed_by && (
                        <Container style={{ gap: theme.spacing(1) }}>
                            <span style={{ fontWeight: 'bold' }}>
                                Completed By:
                            </span>
                            <span
                                style={{ color: theme.palette.text.secondary }}
                            >
                                {ticket.completed_by.name}
                            </span>
                        </Container>
                    )}
                </Container>

                {/* Right Column - Issue Description */}
                <Container
                    style={{
                        flexDirection: 'column',
                        gap: theme.spacing(0.5),
                        flex: 2,
                    }}
                >
                    <span style={{ fontWeight: 'bold' }}>Issue:</span>
                    <div
                        style={{
                            color: theme.palette.text.secondary,
                            maxHeight: '100px',
                            overflowY: 'auto',
                            paddingRight: theme.spacing(1),
                        }}
                    >
                        {ticket.issue_description}
                    </div>
                </Container>
            </Container>

            {/* Bottom Section - Attachments and Actions */}
            <Container
                style={{ flexDirection: 'column', gap: theme.spacing(1) }}
            >
                {/* Attachments Section */}
                {ticket.attachments.length > 0 && (
                    <Container
                        style={{
                            padding: theme.spacing(1),
                            backgroundColor: theme.palette.grey[100],
                            borderRadius: theme.shape.borderRadius,
                            overflowX: 'auto',
                        }}
                    >
                        <Container
                            style={{
                                gap: theme.spacing(1),
                                flexWrap: 'nowrap',
                                width: 'fit-content',
                            }}
                        >
                            {ticket.attachments.map((attachment) => (
                                <div
                                    key={`ATTACHMENT_${attachment.id}`}
                                    style={{}}
                                >
                                    <Attachment
                                        attachment={attachment.file}
                                        uploaded={true}
                                        theme={theme}
                                        size={100}
                                    />
                                </div>
                            ))}
                        </Container>
                    </Container>
                )}

                <SupportTicketTransitionButtons
                    ticket={ticket}
                    handleTransition={handleTransition}
                />
            </Container>
        </Container>
    )
}
